import React from "react";
import { useNavigate } from "react-router-dom";
import { Webhook, Code2, Network, Braces, Clock, Shield, Wifi, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const tools = [
  {
    id: "webhook",
    title: "Webhook Tester",
    description: "Generate unique URLs to receive and debug webhooks with request inspection and replay",
    icon: Webhook,
    path: "/webhook-tester",
    color: "from-cyan-500 to-blue-500"
  },
  {
    id: "base64",
    title: "Base64 Tool",
    description: "Encode and decode text, files, and images with URL-safe options and size comparison",
    icon: Code2,
    path: "/base64",
    color: "from-emerald-500 to-teal-500"
  },
  {
    id: "ip",
    title: "IP Calculator",
    description: "Calculate CIDR subnets, visualize networks, and generate IaC snippets",
    icon: Network,
    path: "/ip-calculator",
    color: "from-violet-500 to-purple-500"
  },
  {
    id: "formatter",
    title: "API Formatter",
    description: "Beautify JSON/XML, flatten structures, and generate TypeScript/Go interfaces",
    icon: Braces,
    path: "/api-formatter",
    color: "from-orange-500 to-red-500"
  },
  {
    id: "timestamp",
    title: "Timestamp Converter",
    description: "Convert Unix timestamps with timezone support, batch processing, and time calculations",
    icon: Clock,
    path: "/timestamp",
    color: "from-amber-500 to-yellow-500"
  },
  {
    id: "ssl",
    title: "SSL/TLS Inspector",
    description: "Check certificate expiration, validate chains, test SSL configs, and generate CSR",
    icon: Shield,
    path: "/ssl-inspector",
    color: "from-green-500 to-emerald-500"
  },
  {
    id: "network",
    title: "Network Tools",
    description: "DNS lookup with NS resolution, PING diagnostics, and traceroute path analysis",
    icon: Wifi,
    path: "/network-tools",
    color: "from-indigo-500 to-blue-500"
  },
  {
    id: "cron",
    title: "Cron Editor",
    description: "Build and validate cron expressions with visual editor and next run predictions",
    icon: Calendar,
    path: "/cron-editor",
    color: "from-purple-500 to-pink-500"
  }
];

export default function Home() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0b] via-[#0f0f12] to-[#0a0a0b]">
      {/* Header */}
      <div className="border-b border-zinc-800/50 backdrop-blur-xl bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Code2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>DevTools Suite</h1>
              <p className="text-sm text-zinc-400">Essential utilities for developers</p>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <main className="max-w-7xl mx-auto px-6 py-16">
        <section className="text-center mb-16 fade-in" aria-labelledby="hero-title">
          <h1 id="hero-title" className="text-5xl lg:text-6xl font-bold mb-6 text-white" style={{fontFamily: 'Space Grotesk'}}>
            Free Developer Tools Suite
            <span className="block mt-2 gradient-text">Built for Speed & Efficiency</span>
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
            A comprehensive suite of 8 powerful tools to streamline your development workflow. Test webhooks, encode data, calculate networks, inspect SSL certificates, and more. Fast, free, and no installation required.
          </p>
        </section>

        {/* Tools Grid */}
        <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" aria-label="Developer tools collection">
          {tools.map((tool, index) => {
            const Icon = tool.icon;
            return (
              <Card
                key={tool.id}
                data-testid={`tool-card-${tool.id}`}
                className="group relative overflow-hidden bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 cursor-pointer hover-glow slide-in"
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => navigate(tool.path)}
              >
                {/* Gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${tool.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                
                <div className="relative p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tool.color} flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <Button
                      data-testid={`launch-${tool.id}-btn`}
                      variant="ghost"
                      size="sm"
                      className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-400/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(tool.path);
                      }}
                    >
                      Launch →
                    </Button>
                  </div>
                  
                  <h2 className="text-xl font-bold text-white mb-3" style={{fontFamily: 'Space Grotesk'}}>
                    {tool.title}
                  </h2>
                  <p className="text-zinc-400 text-sm leading-relaxed">
                    {tool.description}
                  </p>
                </div>
              </Card>
            );
          })}
        </section>

      </main>

      {/* Footer */}
      <div className="border-t border-zinc-800/50 mt-20">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center text-zinc-500 text-sm">
          <p>© 2025 DevTools Suite</p>
        </div>
      </div>
    </div>
  );
}